import React, { useState } from 'react'
import { Input, InputNumber, Select, DatePicker, Button, Tag } from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons'

const { RangePicker } = DatePicker

/**
 * 搜索字段配置
 */
export interface SearchField {
  /** 字段名 */
  name: string
  /** 字段标签 */
  label: string
  /** 字段类型 */
  type: 'input' | 'select' | 'multiSelect' | 'date' | 'dateRange' | 'number' | 'numberRange'
  /** 占位符 */
  placeholder?: string
  /** 选项（用于 select 类型）*/
  options?: Array<{ label: string; value: any }>
  /** 默认值 */
  defaultValue?: any
  /** 字段宽度 */
  width?: number | string
  /** 数字输入最小值 */
  min?: number
  /** 数字输入精度 */
  precision?: number
  /** 日期范围占位符 */
  rangePlaceholders?: [string, string]
  /** 下拉框是否支持搜索 */
  showSearch?: boolean
  /** 下拉框是否在本地过滤选项；false 时由 onSearch 远程搜索 */
  filterOption?: boolean
  /** 下拉框搜索回调 */
  onSearch?: (value: string) => void
  /** 下拉框加载状态 */
  loading?: boolean
  /** 是否在收起状态时隐藏 */
  collapsible?: boolean
}

/**
 * 快捷筛选配置
 */
export interface QuickFilter {
  /** 显示文本 */
  label: string
  /** 筛选值 */
  values: Record<string, any>
  /** 是否默认选中 */
  default?: boolean
}

export interface SearchFormProps {
  /** 搜索字段配置 */
  fields: SearchField[]
  /** 初始值 */
  initialValues?: Record<string, any>
  /** 搜索回调 */
  onSearch: (values: Record<string, any>) => void
  /** 重置回调 */
  onReset?: () => void
  /** 是否显示展开/收起按钮 */
  collapsible?: boolean
  /** 默认是否展开 */
  defaultExpanded?: boolean
  /** 快捷筛选 */
  quickFilters?: QuickFilter[]
  /** Keep quick-filter state and field conditions when either is changed. */
  preserveQuickFilterValues?: boolean
  /** 加载状态 */
  loading?: boolean
  /** 自定义类名 */
  className?: string
}

/**
 * 通用搜索表单组件（Ant Design 版本）
 *
 * 功能：
 * - 多种字段类型支持
 * - 展开/收起
 * - 快捷筛选标签
 * - 搜索/重置按钮
 */
export function SearchForm({
  fields,
  initialValues = {},
  onSearch,
  onReset,
  collapsible = false,
  defaultExpanded = true,
  quickFilters = [],
  preserveQuickFilterValues = false,
  loading = false,
  className = '',
}: SearchFormProps) {
  // 表单值
  const [values, setValues] = useState<Record<string, any>>(() => {
    const initial: Record<string, any> = preserveQuickFilterValues ? { ...initialValues } : {}
    fields.forEach((field) => {
      initial[field.name] = initialValues[field.name] ?? field.defaultValue ?? undefined
    })
    return initial
  })

  // 展开状态
  const [expanded, setExpanded] = useState(defaultExpanded)

  // 选中的快捷筛选
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(() => {
    const defaultFilter = quickFilters.find((f) => f.default)
    return defaultFilter?.label || null
  })

  // 更新字段值
  const handleFieldChange = (name: string, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }))
    if (!preserveQuickFilterValues) setActiveQuickFilter(null)
  }

  // 搜索
  const handleSearch = () => {
    // 过滤掉空值
    const filteredValues: Record<string, any> = {}
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        filteredValues[key] = value
      }
    })
    onSearch(filteredValues)
  }

  // 重置
  const handleReset = () => {
    const initial: Record<string, any> = {}
    fields.forEach((field) => {
      initial[field.name] = field.defaultValue ?? undefined
    })
    const quickValues = preserveQuickFilterValues
      ? quickFilters.find(filter => filter.label === activeQuickFilter)?.values || {}
      : {}
    setValues({ ...initial, ...quickValues })
    if (!preserveQuickFilterValues) setActiveQuickFilter(null)
    onReset?.()
    onSearch(quickValues)
  }

  // 应用快捷筛选
  const handleQuickFilter = (filter: QuickFilter) => {
    setValues((prev) => ({
      ...prev,
      ...filter.values,
    }))
    setActiveQuickFilter(filter.label)
    onSearch(preserveQuickFilterValues ? { ...values, ...filter.values } : filter.values)
  }

  // 可见字段
  const visibleFields = expanded ? fields : fields.filter((f) => !f.collapsible)

  // 是否有可折叠字段
  const hasCollapsibleFields = fields.some((f) => f.collapsible)

  // 渲染字段
  const renderField = (field: SearchField) => {
    const value = values[field.name]
    const commonStyle = { width: field.width || 200 }

    switch (field.type) {
      case 'numberRange':
        return (
          <div className="flex items-center gap-2" style={commonStyle}>
            {[0, 1].map(index => <React.Fragment key={index}>
              {index === 1 && <span className="text-gray-400">至</span>}
              <InputNumber
                aria-label={`${field.label}${index === 0 ? '下限' : '上限'}`}
                value={value?.[index]}
                onChange={val => {
                  const next = [...(value || [null, null])]
                  next[index] = val
                  handleFieldChange(field.name, next)
                }}
                placeholder={field.rangePlaceholders?.[index] || (index === 0 ? '最低' : '最高')}
                style={{ width: 0, flex: 1 }} min={field.min} precision={field.precision}
                onPressEnter={handleSearch}
              />
            </React.Fragment>)}
          </div>
        )

      case 'input':
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder || `请输入${field.label}`}
            prefix={<SearchOutlined />}
            style={commonStyle}
            onPressEnter={handleSearch}
            allowClear
          />
        )

      case 'number':
        return (
          <InputNumber
            value={value}
            onChange={(val) => handleFieldChange(field.name, val)}
            placeholder={field.placeholder || `请输入${field.label}`}
            style={commonStyle}
            onPressEnter={handleSearch}
            min={field.min}
            precision={field.precision}
          />
        )

      case 'select':
        return (
          <Select
            value={value}
            onChange={(val) => handleFieldChange(field.name, val)}
            placeholder={field.placeholder || `请选择${field.label}`}
            options={field.options}
            style={commonStyle}
            showSearch={field.showSearch}
            filterOption={field.filterOption}
            onSearch={field.onSearch}
            loading={field.loading}
            optionFilterProp={
              field.showSearch && field.filterOption !== false ? 'label' : undefined
            }
            allowClear
          />
        )

      case 'multiSelect':
        return (
          <Select
            value={value}
            onChange={(val) => handleFieldChange(field.name, val)}
            placeholder={field.placeholder || `请选择${field.label}`}
            options={field.options}
            mode="multiple"
            style={commonStyle}
            allowClear
          />
        )

      case 'date':
        return (
          <DatePicker
            value={value}
            onChange={(val) => handleFieldChange(field.name, val)}
            placeholder={field.placeholder || '选择日期'}
            style={commonStyle}
            placement="bottomLeft"
          />
        )

      case 'dateRange':
        return (
          <RangePicker
            value={value}
            onChange={(val) => handleFieldChange(field.name, val)}
            style={{ ...commonStyle, width: (field.width as number) || 280 }}
            placeholder={field.rangePlaceholders}
            placement="bottomLeft"
          />
        )

      default:
        return null
    }
  }

  return (
    <div className={`bg-white rounded-modern-lg p-4 shadow-card ${className}`}>
      {/* 快捷筛选 */}
      {quickFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5 pb-4 border-b border-gray-100">
          {quickFilters.map((filter) => (
            <Tag
              key={filter.label}
              color={activeQuickFilter === filter.label ? 'blue' : 'default'}
              onClick={() => handleQuickFilter(filter)}
              style={{ cursor: 'pointer', padding: '4px 12px', fontSize: '13px' }}
            >
              {filter.label}
            </Tag>
          ))}
        </div>
      )}

      {/* 搜索字段 */}
      <div className="flex flex-wrap gap-5 items-end">
        {visibleFields.map((field) => (
          <div key={field.name} className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-gray-600">{field.label}</label>
            {renderField(field)}
          </div>
        ))}

        {/* 操作按钮 */}
        <div className="flex items-center gap-2 ml-auto">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
          >
            搜索
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            重置
          </Button>
          {collapsible && hasCollapsibleFields && (
            <Button
              type="text"
              icon={expanded ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? '收起' : '展开'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
